import { motion } from "framer-motion";

const CHAPTERS = [
  {
    number: "01",
    title: "Unbound Geometry",
    content: "We discard conventional strict grids in favor of spatial depth and organic mathematical resonance.",
  },
  {
    number: "02",
    title: "Tactile Digital",
    content: "Every interaction carries physical inertia, light reflection, and acoustic balance.",
  },
  {
    number: "03",
    title: "Hyper-Performance",
    content: "60FPS WebGL art direction engineered with uncompromising accessibility and elegance.",
  },
];

const reveal = {
  hidden: { y: 60, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative z-10 px-6 py-32 md:px-16 md:py-44 lg:px-24"
    >
      <motion.p
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mb-6 font-mono text-[11px] uppercase tracking-[0.45em] text-[#06B6D4]"
      >
        What Elentara Does
      </motion.p>
      <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
        <div className="lg:sticky lg:top-32 lg:self-start">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            data-testid="manifesto-glass-panel"
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#12141C]/55 p-10 shadow-[0_8px_40px_rgba(0,0,0,0.45)] backdrop-blur-2xl md:p-14"
          >
            <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full border border-[#8B5CF6]/40 [animation:spin_18s_linear_infinite]" />
            <div className="pointer-events-none absolute -bottom-10 -left-10 h-24 w-24 rotate-45 border border-[#D4AF37]/30 [animation:spin_26s_linear_infinite_reverse]" />
            <div className="pointer-events-none absolute right-10 bottom-10 h-3 w-3 rounded-full bg-[#06B6D4] shadow-[0_0_24px_6px_rgba(6,182,212,0.5)]" />
            <h2 className="font-display text-3xl font-bold leading-tight text-[#F4F6FB] md:text-5xl">
              We design and build{" "}
              <span className="bg-gradient-to-r from-[#8B5CF6] via-[#06B6D4] to-[#D4AF37] bg-clip-text text-transparent">
                what shouldn't exist yet.
              </span>
            </h2>
            <p className="mt-8 max-w-sm text-sm leading-relaxed text-[#F4F6FB]/55 md:text-base">
              Elentara is a creative technology studio working across immersive web, 3D and motion,
              spatial computing and interactive systems — design and engineering held under one
              creative direction, from the first question to the final frame.
            </p>
          </motion.div>
        </div>
        <div>
          {CHAPTERS.map((chapter, i) => (
            <motion.article
              key={chapter.number}
              data-testid={`manifesto-chapter-${chapter.number}`}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.1 }}
              className="group border-t border-white/10 py-12 transition-colors duration-500 first:border-t-0 first:pt-0 hover:border-[#8B5CF6]/40 md:py-14"
            >
              <span className="text-outline font-display block text-6xl font-black leading-none transition-all duration-500 group-hover:[-webkit-text-stroke-color:rgba(139,92,246,0.7)] md:text-8xl">
                {chapter.number}
              </span>
              <h3 className="font-display mt-6 text-xl font-semibold text-[#F4F6FB] md:text-2xl">
                {chapter.title}
              </h3>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-[#F4F6FB]/55 md:text-base">
                {chapter.content}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
